import { getSiteApi } from '@/api-calls/sites/get'
import {
  getPendingInvitationsApi,
  getStaffMembersApi,
} from '@/api-calls/staff/get'
import {
  cancelInvitationApi,
  inviteStaffMemberApi,
  removeStaffMemberApi,
  updateStaffRoleApi,
} from '@/api-calls/staff/mutate'
import { useAuth } from '@/auth/auth-context'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@blogging/ui'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import {
  Building2,
  Check,
  Copy,
  Globe,
  Loader2,
  Mail,
  MoreVertical,
  Plus,
  Users,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

interface StaffMember {
  id: string
  userId: string
  role: string
  user: {
    id: string
    name: string | null
    email: string
    image: string | null
  }
  invitedBy: string | null
  createdAt: string
}

interface Invitation {
  id: string
  email: string
  role: string
  status: string
  token: string
  invitedBy: string
  inviter: {
    name: string | null
    email: string
  }
  expiresAt: string
  createdAt: string
}

interface Site {
  id: string
  name: string | null
  slug: string
  description: string | null
}

const roleColors = {
  admin: 'destructive',
  editor: 'default',
  author: 'secondary',
} as const

const roleDescriptions = {
  admin: 'Full access to manage site, content, and team',
  editor: 'Manage all posts, pages, and tags',
  author: 'Create and manage own posts',
}

export function SiteSettingsStaffPage() {
  const { siteId } = useParams()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false)
  const [selectedMember, setSelectedMember] = useState<StaffMember | null>(null)
  const [reassignToUserId, setReassignToUserId] = useState<string>('none')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('author')
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(
    null,
  )

  const { data: siteData, isLoading: isLoadingSite } = useFetch(getSiteApi, {
    resourceName: 'site',
    initialParams: { id: siteId as string },
  })
  const site = siteData as Site | undefined

  const {
    data: staffMembers,
    isLoading: isLoadingStaff,
    error: staffError,
  } = useFetch(getStaffMembersApi, {
    resourceName: 'staff',
    initialParams: { siteId: siteId as string },
  })

  const {
    data: invitations,
    isLoading: isLoadingInvitations,
    error: invitationsError,
  } = useFetch(getPendingInvitationsApi, {
    resourceName: 'invitations',
    initialParams: { siteId: siteId as string },
  })

  const { mutate: inviteStaff, isPending: isInviting } = useMutation(
    inviteStaffMemberApi,
    {
      resourceName: 'invitation',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getPendingInvitationsApi.apiName, { siteId }],
        })
        setIsInviteDialogOpen(false)
        setInviteEmail('')
        setInviteRole('author')
        toast.success('Invitation sent successfully!')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to send invitation')
      },
    },
  )

  const { mutate: updateRole, isPending: isUpdatingRole } = useMutation(
    updateStaffRoleApi,
    {
      resourceName: 'staff',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getStaffMembersApi.apiName, { siteId }],
        })
        toast.success('Role updated successfully!')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to update role')
      },
    },
  )

  const { mutate: removeStaff, isPending: isRemoving } = useMutation(
    removeStaffMemberApi,
    {
      resourceName: 'staff',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getStaffMembersApi.apiName, { siteId }],
        })
        setIsRemoveDialogOpen(false)
        setSelectedMember(null)
        setReassignToUserId('none')
        toast.success('Staff member removed successfully!')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to remove staff member')
      },
    },
  )

  const { mutate: cancelInvitation, isPending: isCancelling } = useMutation(
    cancelInvitationApi,
    {
      resourceName: 'invitation',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getPendingInvitationsApi.apiName, { siteId }],
        })
        toast.success('Invitation cancelled')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to cancel invitation')
      },
    },
  )

  const handleInvite = () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }
    inviteStaff({
      siteId: siteId as string,
      email: inviteEmail.trim(),
      role: inviteRole,
    })
  }

  const handleRemoveMember = () => {
    if (selectedMember) {
      removeStaff({
        siteId: siteId as string,
        userId: selectedMember.userId,
        reassignToUserId:
          reassignToUserId === 'none' ? undefined : reassignToUserId,
      })
    }
  }

  const reassignableMembers = (staffMembers || []).filter(
    (member: StaffMember) => member.userId !== selectedMember?.userId,
  )

  const handleChangeRole = (member: StaffMember, newRole: string) => {
    updateRole({
      siteId: siteId as string,
      userId: member.userId,
      role: newRole,
    })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getInitials = (name: string | null) => {
    if (!name) return '?'
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name[0].toUpperCase()
  }

  const copyInvitationLink = async (invitation: Invitation) => {
    const invitationUrl = `${window.location.origin}/invitations/${invitation.token}`
    try {
      await navigator.clipboard.writeText(invitationUrl)
      setCopiedInvitationId(invitation.id)
      toast.success('Invitation link copied to clipboard!')
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopiedInvitationId(null), 2000)
    } catch (err) {
      toast.error('Failed to copy link')
    }
  }

  if (isLoadingStaff) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <Skeleton className="h-48 w-full rounded-lg" />
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  if (staffError) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <div className="flex flex-col items-center justify-center p-8">
          <p className="text-destructive">Error loading staff members</p>
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-8">
        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold">Staff</h1>
          <p className="text-muted-foreground">
            Manage team members who can collaborate on your site.
          </p>
        </div>

        {/* Info Card */}
        <div className="rounded-lg border bg-blue-50 dark:bg-blue-950 p-4">
          <div className="flex items-start gap-3">
            <Users className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Team Collaboration
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Invite team members to collaborate on your site.{' '}
                <strong>Admin</strong> can manage everything,
                <strong> Editor</strong> can manage all content, and{' '}
                <strong>Author</strong> can manage their own posts.
              </p>
            </div>
          </div>
        </div>

        {/* Staff Members Table */}
        <div className="rounded-lg border bg-card">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold">Team Members</h3>
            <Button onClick={() => setIsInviteDialogOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Invite Member
            </Button>
          </div>

          {staffMembers && staffMembers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staffMembers.map((member: StaffMember) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={member.user.image || undefined} />
                          <AvatarFallback>
                            {getInitials(member.user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{member.user.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {member.user.email}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          roleColors[member.role as keyof typeof roleColors]
                        }
                      >
                        {member.role.charAt(0).toUpperCase() +
                          member.role.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(member.createdAt)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleChangeRole(member, 'admin')}
                            disabled={member.role === 'admin' || isUpdatingRole}
                          >
                            Make Admin
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleChangeRole(member, 'editor')}
                            disabled={
                              member.role === 'editor' || isUpdatingRole
                            }
                          >
                            Make Editor
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleChangeRole(member, 'author')}
                            disabled={
                              member.role === 'author' || isUpdatingRole
                            }
                          >
                            Make Author
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedMember(member)
                              setReassignToUserId('none')
                              setIsRemoveDialogOpen(true)
                            }}
                            className="text-destructive"
                          >
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No staff members yet</p>
              <Button
                onClick={() => setIsInviteDialogOpen(true)}
                variant="outline"
                size="sm"
                className="mt-4"
              >
                <Plus className="h-4 w-4 mr-2" />
                Invite Your First Member
              </Button>
            </div>
          )}
        </div>

        {/* Pending Invitations */}
        {invitations && invitations.length > 0 && (
          <div className="rounded-lg border bg-card">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Pending Invitations</h3>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Invited By</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((invitation: Invitation) => {
                  const isExpired = new Date(invitation.expiresAt) < new Date()
                  const isCopied = copiedInvitationId === invitation.id
                  return (
                    <TableRow key={invitation.id}>
                      <TableCell className="font-medium">
                        {invitation.email}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            roleColors[
                              invitation.role as keyof typeof roleColors
                            ]
                          }
                        >
                          {invitation.role.charAt(0).toUpperCase() +
                            invitation.role.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {invitation.inviter.name || invitation.inviter.email}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {isExpired ? (
                          <span className="text-destructive">Expired</span>
                        ) : (
                          formatDate(invitation.expiresAt)
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => copyInvitationLink(invitation)}
                            title="Copy invitation link"
                          >
                            {isCopied ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              cancelInvitation({
                                siteId: siteId as string,
                                invitationId: invitation.id,
                              })
                            }
                            disabled={isCancelling}
                            title="Cancel invitation"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Invite Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an email invitation to add a new team member to your site.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Site & Inviter Information */}
            {(site || user) && (
              <div className="rounded-lg border bg-card p-4 space-y-4">
                {/* Site Information */}
                {site && (
                  <div className="flex items-start gap-3 pb-3 border-b">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                        Inviting to
                      </p>
                      <p className="text-base font-bold truncate">
                        {site.name || site.slug}
                      </p>
                      {site.name && site.slug && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Globe className="h-3 w-3 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground truncate">
                            {site.slug}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                They'll receive an invitation email with a link to join your
                site.
              </p>
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="author">
                    <div>
                      <div className="font-medium">Author</div>
                      <div className="text-xs text-muted-foreground">
                        {roleDescriptions.author}
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="editor">
                    <div>
                      <div className="font-medium">Editor</div>
                      <div className="text-xs text-muted-foreground">
                        {roleDescriptions.editor}
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div>
                      <div className="font-medium">Admin</div>
                      <div className="text-xs text-muted-foreground">
                        {roleDescriptions.admin}
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsInviteDialogOpen(false)}
              disabled={isInviting}
            >
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={isInviting}>
              {isInviting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Dialog */}
      <AlertDialog
        open={isRemoveDialogOpen}
        onOpenChange={setIsRemoveDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <strong>{selectedMember?.user.name}</strong> from your site? They
              will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {reassignableMembers.length > 0 && (
            <div className="space-y-2 py-2">
              <Label htmlFor="reassign-to">
                Reassign their posts &amp; tags to
              </Label>
              <Select
                value={reassignToUserId}
                onValueChange={setReassignToUserId}
              >
                <SelectTrigger id="reassign-to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Don't reassign</SelectItem>
                  {reassignableMembers.map((member: StaffMember) => (
                    <SelectItem key={member.userId} value={member.userId}>
                      {member.user.name || member.user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Posts, tags, and bylines they created will move to this
                person. Leave as "Don't reassign" to keep them as-is.
              </p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ContainerWithSidebarAndNoTopbar>
  )
}
